import PersonAdd from "@mui/icons-material/PersonAdd";
import { Box, Alert, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { SignatureType, Permission } from "generated-graphql/graphql";
import { SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { useMemo } from "react";
import RemoveFilled from "@mui/icons-material/Remove";
import { DataGridWithAddButton } from "./DataGridWithAddButton";
import { prettyPrintDate } from "util/dates";
import { useReviewerIssues } from "contexts/SubmissionFormContext";
import { IssueCount } from "components/IssueCount";
import { DateTime } from "luxon";
import { useAuthorization } from "hooks/useAuthorization";
import { useTenant } from "hooks/useTenant";
import { renderIssueMessage } from "./helpers/renderIssueMessage";

type SignersDatagridProps = {
  signers: SubmissionReviewerSigner[];
  signatureType: SignatureType;
  handleAddSigner: () => void;
  handleRemoveSigner: (signer: SubmissionReviewerSigner) => void;
  submissionDueDate?: DateTime | null;
};

export function SignersDatagrid({
  signers,
  signatureType,
  handleAddSigner,
  handleRemoveSigner,
  submissionDueDate,
}: SignersDatagridProps) {
  const issues = useReviewerIssues();
  const { hasPermissions } = useAuthorization();
  const { tenantId } = useTenant();

  const canManageUsers = hasPermissions([Permission.WriteAllUserTenant]);

  const signerIssues = useMemo(
    () =>
      issues.filter((i) =>
        signers.some((s) =>
          i.schemaStack?.some((schemaStack) => schemaStack.name === s.id)
        )
      ),
    [issues, signers]
  );
  const columns = useMemo(() => {
    const columns: GridColDef<SubmissionReviewerSigner>[] = [
      {
        field: "name",
        headerName: "Signer Name",
        display: "flex",
        flex: 0.65,
        sortable: false,
      },
    ];

    if (signatureType === SignatureType.Digital) {
      columns.push({
        field: "email",
        headerName: "Email",
        display: "flex",
        sortable: false,
        flex: 1,
      });
    }

    if (signatureType === SignatureType.WetInk) {
      columns.push({
        field: "address",
        headerName: "Address",
        display: "flex",
        sortable: false,
        flex: 1,
      });
    }

    // Always add dueDate as the second-to-last column
    columns.push({
      field: "dueDate",
      display: "flex",
      flex: 1,
      headerName: "Due Date",
      sortable: false,
      valueGetter: (_, row) =>
        row.dueDate ? prettyPrintDate(row.dueDate.toISO()) : "",
    });
    if (signerIssues.length > 0) {
      columns.push({
        field: "issues",
        display: "flex",
        flex: 0.6,
        headerName: "Issues",
        renderCell: ({ row }) => {
          const rowIssues = signerIssues.filter((i) => {
            return i.schemaStack?.some((s) => s.name === row.id);
          });
          return <IssueCount issueCount={rowIssues.length} />;
        },
      });
    }

    // Always add actions as the last column
    columns.push({
      field: "actions",
      display: "flex",
      headerName: "",
      flex: 0.25,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <GridActionsCellItem
              label="Delete"
              icon={<RemoveFilled />}
              onClick={(event) => {
                event.stopPropagation();
                handleRemoveSigner(row);
              }}
            />
          </Box>
        );
      },
    });
    return columns;
  }, [signatureType, handleRemoveSigner, signerIssues]);

  return (
    <Box>
      {signerIssues.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {signerIssues.map((i, index) => (
            <Typography key={index} variant="body2">
              {renderIssueMessage(i.message, canManageUsers, tenantId)}
            </Typography>
          ))}
        </Alert>
      )}
      <DataGridWithAddButton
        rows={signers}
        columns={columns}
        addButtonConfig={{
          buttonText: "Add Signer",
          icon: <PersonAdd />,
          colSpan: 3,
        }}
        onAdd={handleAddSigner}
        createEmptyRow={() => ({
          id: "empty",
          name: "",
          email: "",
          address: "",
          order: 0,
          isStaff: false,
        })}
      />
    </Box>
  );
}
